import { useEffect, useState } from "react";

import { CardsService } from "../services/cardsService";

// The pause only affects one card provider, so whether it applies depends on the
// organization the cards belong to — the backend resolves that (the provider
// never reaches the client) and answers with a boolean.
//
// Pass the organization in context. While it is still unknown (data loading, no
// holder picked yet) nothing is gated and no request goes out: the gating is a
// courtesy — the backend rejects paused operations regardless — so neither a
// missing organization nor a failed check may block normal operations.
const STALE_AFTER_MS = 10 * 60_000;

const cache = new Map<string, { frozen: boolean; fetchedAt: number }>();
const inflight = new Map<string, Promise<boolean>>();

async function ensureStatus(organizationId: string): Promise<boolean> {
  const cached = cache.get(organizationId);
  if (cached && Date.now() - cached.fetchedAt < STALE_AFTER_MS) {
    return cached.frozen;
  }

  let pending = inflight.get(organizationId);
  if (!pending) {
    pending = CardsService.getMaintenanceStatus(organizationId)
      .then((response) => {
        const frozen = response.data?.balance_operations_frozen === true;
        cache.set(organizationId, { frozen, fetchedAt: Date.now() });
        return frozen;
      })
      .finally(() => {
        inflight.delete(organizationId);
      });
    inflight.set(organizationId, pending);
  }

  return pending;
}

export function useCardOperationsMaintenance(organizationId?: string | null): {
  movementsFrozen: boolean;
  isLoading: boolean;
} {
  const [answer, setAnswer] = useState<{ organizationId: string; frozen: boolean } | null>(null);

  useEffect(() => {
    if (!organizationId) return;

    let cancelled = false;
    ensureStatus(organizationId)
      .then((frozen) => {
        if (!cancelled) setAnswer({ organizationId, frozen });
      })
      .catch(() => {
        if (!cancelled) setAnswer({ organizationId, frozen: false });
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  // Answers are keyed by organization so a previous one is never carried over
  // to a different organization while its own answer is in flight.
  const current = organizationId && answer?.organizationId === organizationId ? answer : null;

  return {
    movementsFrozen: current?.frozen ?? false,
    isLoading: Boolean(organizationId) && current === null,
  };
}
